import type { IModalConfig, TypeModalDialogConfig } from './types';
import type { TypeBuildReturn as TypeButtonBuildReturn } from '../button/types';

import * as exHelper from '../../helper';

import addClass from '../../general/class/addClass';
import child from '../../general/child';
import isEmptyArray from '../../general/isEmpty/isEmptyArray';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';
import prependChild from '../../general/prepend/prependChild';

import exExternal from '../../external';
import * as exBadge from '../badge';
import * as exButton from '../button';
import * as exElement from '../../utils/element';
import * as exEvent from '../../utils/event';
import * as exIcon from '../../utils/icon';

import * as exUtils from './utils';

// ----------------------------------------------------------------------------

export type TypeBuildReturn = {
    element: HTMLElement;
    elements: {
        element: HTMLElement;
        dialog: HTMLElement;
        content: HTMLElement;
        header?: HTMLElement;
        body: HTMLElement;
        footer?: HTMLElement;
        buttons?: TypeButtonBuildReturn[];
    };
    bs?: any;
    child: (arr: any) => HTMLElement;
};

// ----------------------------------------------------------------------------

function create(config: IModalConfig = {}): TypeBuildReturn {
    const defConfig: IModalConfig = {
        allowDismiss: true,
        bootstrap: {
            enabled: true,
        },
    };
    exHelper.merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const build = {
        elements: {},
    } as TypeBuildReturn;

    // ------------------------------------------------------------------------

    // Create element (modal)
    const elModal = exElement.create({
        merge: defConfig,
        class: {
            new: 'modal fade',
        },
    });
    build.elements.element = elModal;
    build.element = elModal;

    // ------------------------------------------------------------------------

    // Create element (modal dialog)
    const elModalDialog = createModalDialog(defConfig.modalDialog);
    elModal.appendChild(elModalDialog);
    build.elements.dialog = elModalDialog;

    // Create element (modal content)
    const elModalContent = exElement.create({
        merge: defConfig.modalContent,
        class: {
            new: 'modal-content',
        },
    });
    elModalDialog.appendChild(elModalContent);
    build.elements.content = elModalContent;

    // If using modal header
    if (defConfig.modalHeader || defConfig.allowDismiss === true) {
        // Create element (modal header)
        const elModalHeader = exElement.create({
            merge: defConfig.modalHeader,
            class: {
                new: 'modal-header',
            },
        });
        elModalContent.appendChild(elModalHeader);
        build.elements.header = elModalHeader;
    }

    // Create element (modal body)
    const elModalBody = exElement.create({
        merge: defConfig.modalBody,
        class: {
            new: 'modal-body',
        },
    });
    elModalContent.appendChild(elModalBody);
    build.elements.body = elModalBody;

    // If using modal footer
    if (defConfig.modalFooter || !isEmptyArray(defConfig.buttons)) {
        // Create element (modal footer)
        const elModalFooter = exElement.create({
            merge: defConfig.modalFooter,
            class: {
                new: 'modal-footer',
            },
        });
        elModalContent.appendChild(elModalFooter);
        build.elements.footer = elModalFooter;
    }

    // ------------------------------------------------------------------------

    // Settings
    settings(build, defConfig);

    // Events
    events(build, defConfig);

    // Methods
    methods(build, defConfig);

    // ------------------------------------------------------------------------

    // If using bootstrap
    if (defConfig.bootstrap?.enabled === true) {
        // Bootstrap (modal)
        const bsModal = new exExternal.bootstrap.Modal(build.element, defConfig.bootstrap);
        build.bs = bsModal;
    }

    // ------------------------------------------------------------------------

    // Return
    return build;
}

function createModalDialog(config: TypeModalDialogConfig = {}): HTMLElement {
    const elModalDialog = exElement.create({
        merge: config,
        class: {
            new: 'modal-dialog',
        },
    });

    if (config.centered === true) {
        addClass(elModalDialog, 'modal-dialog-centered');
    }

    return elModalDialog;
}

function settings(build: TypeBuildReturn, config: IModalConfig = {}): void {
    const elements = build.elements;

    // If using width
    if (config.width != null) {
        elements.dialog.style.maxWidth = config.width;
    }

    // If using backdrop
    if (config.backdrop != null) {
        elements.element.dataset.bsBackdrop = String(config.backdrop);
    }

    // ------------------------------------------------------------------------

    // If using modal header
    if (elements.header && config.modalHeader && !isEmptyObject(config.modalHeader)) {
        const itemModalHeader = config.modalHeader;

        // If using modal title
        if (itemModalHeader.title || !isEmptyObject(itemModalHeader.title)) {
            if (typeof itemModalHeader.title === 'string') {
                itemModalHeader.title = {
                    html: itemModalHeader.title,
                };
            }

            // Create element (modal title)
            const elTitle = exElement.create({
                merge: itemModalHeader.title,
                tag: 'h5',
                class: {
                    new: 'modal-title',
                },
            });
            elements.header.appendChild(elTitle);

            // If using type
            if (itemModalHeader.type && !itemModalHeader.icon) {
                // Switch Type
                switch (itemModalHeader.type) {
                    case 'success':
                        itemModalHeader.icon = 'fa-solid fa-check';
                        break;
                    case 'warning':
                        itemModalHeader.icon = 'fa-solid fa-exclamation-triangle';
                        break;
                    case 'danger':
                        itemModalHeader.icon = 'fa-solid fa-exclamation';
                        break;
                    // No default
                }
            }

            // If using type and icon
            if (itemModalHeader.type && itemModalHeader.icon) {
                // Create element (badge)
                const elSpan = exBadge.create({
                    type: itemModalHeader.type,
                });
                prependChild(elements.header, elSpan);

                // Create element (icon)
                const elIcon = exIcon.create(itemModalHeader.icon);
                elSpan.appendChild(elIcon);
            }
        }
    }

    // ------------------------------------------------------------------------

    // If using allow dismiss
    if (elements.header && config.allowDismiss === true) {
        // Create element (button close)
        const buttonClose = exButton.create({
            class: {
                new: 'btn-close',
            },
            dataset: {
                bsDismiss: 'modal',
            },
        });
        elements.header.appendChild(buttonClose.element);
    }

    // ------------------------------------------------------------------------

    // If using buttons
    if (elements.footer && config.buttons && !isEmptyArray(config.buttons)) {
        elements.buttons = [];

        for (const item of config.buttons) {
            const clickHandler = exUtils.buildClickHandler(item);

            if (clickHandler !== undefined) {
                item.event = { ...item.event, click: clickHandler };
            }

            const button = exButton.create(item);
            elements.footer.appendChild(button.element);
            elements.buttons.push(button);
        }
    }
}

function events(build: TypeBuildReturn, config: IModalConfig = {}): void {
    const elements = build.elements;

    // Create event (shown modal)
    elements.element.addEventListener('shown.bs.modal', function (this: any): any {
        if (this.actionModal === true) {
            exUtils.hide(this);

            // Set back action modal
            this.actionModal = false;
        }
    });

    // ------------------------------------------------------------------------

    // If using modal event
    if (config.modalEvent && !isEmptyObject(config.modalEvent)) {
        for (const key of Object.keys(config.modalEvent)) {
            const value = config.modalEvent[key];

            const type = key + '.bs.modal';

            // Create event
            exEvent.create(elements.element, {
                [type]: value,
            });
        }
    }

    // ------------------------------------------------------------------------

    // If using destroy
    if (config.destroy === true) {
        // Show modal and add handler that will remove it from DOM once closed
        elements.element.addEventListener('hidden.bs.modal', function (this: any): any {
            this.remove();
        });
    }
}

function methods(build: TypeBuildReturn, config: IModalConfig = {}): void {
    const elements = build.elements;

    build.child = function (arr): HTMLElement {
        child(elements.body, arr);

        return elements.element;
    };
}

// ----------------------------------------------------------------------------

export { create };
